import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Spin, Modal, Avatar, notification, Input } from 'antd';
import {
  BankOutlined, BellFilled, BulbOutlined, CheckCircleOutlined, CheckOutlined, ClockCircleOutlined,
  CloseOutlined, CompassOutlined, CustomerServiceOutlined, EditOutlined, EnvironmentOutlined, EyeOutlined,
  FilePdfOutlined, FileTextOutlined, HeartOutlined, IdcardOutlined, LikeOutlined,
  LinkOutlined, LoadingOutlined, MessageOutlined, NotificationOutlined, PhoneOutlined,
  PlusOutlined, SearchOutlined, TrophyOutlined, UserAddOutlined, UsergroupAddOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G, BG } from '../shared/constants';
import { authHeader, useLang, ssGet, ssSet, countryFlag, inrToForeign } from '../shared/utils';
import { useDebounce, useMobile } from '../shared/hooks';
import { MembershipCard } from '../components/MembershipCard';
import { PostCard, PostDetailModal, InlineCommentSection, WritePostModal, timeAgo, CAT_META, CATS, LastSeenBadge } from '../components/PostCard';
import { TodayCard, YearlyCard, MonthlyCard } from './Community';
import { CityCommunityTab } from './Village';
import { MemberProfileModal } from '../components/MemberProfileModal';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

// ── Community Board Tab ───────────────────────────────────────────────────────
export function CommunityBoardTab({ profile, onSwitchTab, initMyView = false, onInitMyViewDone }) {
  const [posts,       setPosts]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [category,    setCategory]    = useState('');
  const [page,        setPage]        = useState(1);
  const [total,       setTotal]       = useState(0);
  const [openPost,    setOpenPost]    = useState(null);
  const [writeOpen,     setWriteOpen]     = useState(false);
  const [myPosts,       setMyPosts]       = useState([]);
  const [myView,        setMyView]        = useState(false);
  const [canWrite,      setCanWrite]      = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [requesting,    setRequesting]    = useState(false);
  // Highlight cards data
  const [todayData,   setTodayData]   = useState(null);
  const [monthData,   setMonthData]   = useState(null);
  const [yearData,    setYearData]    = useState(null);
  const myId = profile?.id;

  useEffect(() => {
    // Fetch contribution summary for highlight cards
    axios.get(`${baseUrl}/member/contribution-summary`, { headers: authHeader() })
      .then(r => {
        if (r.data.status) {
          const d = r.data.result;
          setTodayData({ today_amount: d.today_total,  today_count: d.today_count });
          setMonthData({ month_amount: d.month_total,  month_count: d.month_count });
          setYearData({ year_amount: d.fy_total, active_members: d.active_members, dev_works: 0 });
        }
      }).catch(() => {});
    // Top monthly contributor
    axios.get(`${baseUrl}/member/leaderboard?period=month&limit=1`, { headers: authHeader() })
      .then(r => {
        if (r.data.status && r.data.result?.length) {
          const top = r.data.result[0];
          setMonthData(prev => prev ? { ...prev, top_name: top.name, top_amount: top.total_amount } : null);
        }
      }).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    // Check write permission via dedicated GET — no dummy POST needed
    axios.get(`${baseUrl}/member/post-access-status`, { headers: authHeader() })
      .then(r => {
        if (r.data.status) {
          const rs = r.data.request_status || null;
          setRequestStatus(rs);
          // User can post only if their access request is approved
          setCanWrite(rs === 'approved');
        }
        // If no request yet or pending → canWrite stays false (default)
      })
      .catch(() => setCanWrite(false));
  }, []); // eslint-disable-line

  const sendRequest = async () => {
    setRequesting(true);
    try {
      const res = await axios.post(`${baseUrl}/member/request-post-access`, {}, { headers: authHeader() });
      if (res.data.status) {
        setRequestStatus('pending');
        notification.success({ message: res.data.message });
      }
    } catch (err) {
      notification.error({ message: err.response?.data?.message || 'Error' });
    } finally { setRequesting(false); }
  };

  const fetchPosts = useCallback(async (pg = 1, cat = category) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 10 });
      if (cat) params.set('category', cat);
      const res = await axios.get(`${baseUrl}/member/posts?${params}`, { headers: authHeader() });
      if (res.data.status) {
        setPosts(pg === 1 ? res.data.result : prev => [...prev, ...res.data.result]);
        setTotal(res.data.total);
      }
    } catch {} finally { setLoading(false); }
  }, [category]); // eslint-disable-line

  const fetchMyPosts = useCallback(async () => {
    const res = await axios.get(`${baseUrl}/member/posts/mine`, { headers: authHeader() });
    if (res.data.status) setMyPosts(res.data.result || []);
  }, []);

  useEffect(() => { fetchPosts(1); }, [category]); // eslint-disable-line

  // Auto-open my posts view when navigated from activity notification
  useEffect(() => {
    if (initMyView) {
      setMyView(true);
      fetchMyPosts();
      onInitMyViewDone?.();
    }
  }, []); // eslint-disable-line

  const handleReact = async (postId, reaction) => {
    try {
      await axios.post(`${baseUrl}/member/posts/${postId}/react`, { reaction }, { headers: authHeader() });
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const removing = p.my_reaction === reaction;
        return {
          ...p,
          my_reaction: removing ? null : reaction,
          reaction_count: removing ? (p.reaction_count - 1) : (p.my_reaction ? p.reaction_count : p.reaction_count + 1),
        };
      }));
    } catch {}
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage);
  };

  const hasMore = posts.length < total;

  const MyPostsBadge = (s) => {
    if (s === 'approved')  return <span style={{ fontSize: 9, background: '#dcfce7', color: '#15803d', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>Published</span>;
    if (s === 'pending')   return <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>Review में</span>;
    if (s === 'rejected')  return <span style={{ fontSize: 9, background: '#fee2e2', color: '#991b1b', borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>Rejected</span>;
    return null;
  };

  return (
    <div>
      {/* Header */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        border: `1px solid #e5e7eb`,
        borderLeft: `4px solid ${M}`,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: M, letterSpacing: 0.2 }}>समाज मंच</div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
            Community Board — विचार, समाचार, रोजगार
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canWrite === true && (
            <button onClick={() => { setMyView(false); setWriteOpen(true); }} style={{
              background: M, border: 'none', borderRadius: 8, padding: '7px 14px',
              fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <EditOutlined /> पोस्ट लिखें
            </button>
          )}
          {canWrite === false && (
            <button onClick={requestStatus === 'pending' ? undefined : sendRequest}
              disabled={requesting || requestStatus === 'pending'}
              style={{
                background: requestStatus === 'pending' ? '#f3f4f6' : `${M}10`,
                border: `1px solid ${requestStatus === 'pending' ? '#e5e7eb' : M}`,
                borderRadius: 8, padding: '7px 14px', fontSize: 11, fontWeight: 600,
                color: requestStatus === 'pending' ? '#9ca3af' : M,
                cursor: requestStatus === 'pending' ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
              {requesting ? <LoadingOutlined /> : requestStatus === 'pending'
                ? <><ClockCircleOutlined /> अनुरोध भेजा गया</>
                : requestStatus === 'rejected'
                  ? <><CheckOutlined /> पुनः अनुरोध करें</>
                  : <><NotificationOutlined /> पोस्ट अनुमति मांगें</>}
            </button>
          )}
          {canWrite !== false && (
            <button onClick={() => { setMyView(!myView); if (!myView) fetchMyPosts(); }} style={{
              background: myView ? `${M}10` : '#f9fafb',
              border: `1px solid ${myView ? M : '#e5e7eb'}`,
              borderRadius: 8, padding: '7px 12px', fontSize: 11,
              fontWeight: 600, color: myView ? M : '#6b7280', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <FileTextOutlined /> मेरी Posts
            </button>
          )}
        </div>
      </div>

      {/* My posts view */}
      {myView ? (
        <div>
          {myPosts.length === 0
            ? <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
                अभी कोई पोस्ट नहीं है।
              </div>
            : myPosts.map(p => {
              const cat = CAT_META[p.category] || CAT_META.thought;
              return (
                <div key={p.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: cat.bg, color: cat.color, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {cat.icon} {cat.label}
                        </span>
                        {MyPostsBadge(p.status)}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{p.title}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2, display: 'flex', gap: 10 }}>
                        <span><EyeOutlined /> {p.views}</span>
                        <span><LikeOutlined /> {p.reaction_count}</span>
                        <span><MessageOutlined /> {p.comment_count}</span>
                      </div>
                    </div>
                    {p.image_url && (
                      <img src={p.image_url} alt={p.title}
                        style={{ width: 60, height: 50, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                    )}
                  </div>
                </div>
              );
            })
          }
        </div>
      ) : (
        <>
          {/* Category filter */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 14, scrollbarWidth: 'none' }}>
            <button onClick={() => { setCategory(''); setPage(1); }} style={{
              flexShrink: 0, background: !category ? M : '#fff',
              border: `1px solid ${!category ? M : '#e5e7eb'}`, borderRadius: 6,
              padding: '5px 14px', fontSize: 11, fontWeight: 700,
              color: !category ? '#fff' : '#6b7280', cursor: 'pointer',
            }}>
              सभी
            </button>
            {CATS.map(c => (
              <button key={c.key} onClick={() => { setCategory(c.key); setPage(1); }} style={{
                flexShrink: 0, background: category === c.key ? c.bg : '#fff',
                border: `1px solid ${category === c.key ? c.color : '#e5e7eb'}`, borderRadius: 6,
                padding: '5px 14px', fontSize: 11, fontWeight: 700,
                color: category === c.key ? c.color : '#6b7280', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>

          {/* Feed */}
          {loading && posts.length === 0
            ? <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
            : posts.length === 0
              ? (
                <div style={{
                  textAlign: 'center', padding: '50px 20px',
                  background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0',
                }}>
                  <EditOutlined style={{ fontSize: 36, color: '#d1d5db', marginBottom: 14 }} />
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    अभी कोई पोस्ट नहीं है
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 20 }}>
                    {category ? `"${CAT_META[category]?.label}" श्रेणी में` : 'समाज मंच पर'} पहली पोस्ट आप लिखें
                  </div>
                  {canWrite === true && (
                    <button onClick={() => setWriteOpen(true)} style={{
                      background: M, border: 'none', borderRadius: 8, padding: '9px 24px',
                      fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                    }}>
                      <EditOutlined /> पहली पोस्ट लिखें
                    </button>
                  )}
                </div>
              )
              : posts.reduce((acc, p, i) => {
                  // Inject Today card before first post
                  if (i === 0 && todayData && !category) acc.push(<TodayCard key="today" data={todayData} onSwitchTab={onSwitchTab} />);
                  // Inject Yearly card after 2nd post
                  if (i === 2 && yearData && !category)  acc.push(<YearlyCard key="yearly" data={yearData} />);
                  // Inject Monthly card after every 4th post
                  if (i > 0 && i % 4 === 0 && monthData && !category) acc.push(<MonthlyCard key={`month-${i}`} data={monthData} />);
                  acc.push(<PostCard key={p.id} post={p} onOpen={setOpenPost} onReact={handleReact} />);
                  return acc;
                }, [])
          }

          {hasMore && (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <Button onClick={loadMore} loading={loading} style={{ borderRadius: 20 }}>
                और देखें
              </Button>
            </div>
          )}
        </>
      )}

      {/* Write modal */}
      <WritePostModal open={writeOpen} onClose={() => setWriteOpen(false)} onPosted={() => fetchPosts(1)} />

      {/* Detail modal */}
      {openPost && (
        <PostDetailModal
          post={openPost}
          onClose={() => setOpenPost(null)}
          myUserId={myId}
        />
      )}
    </div>
  );
}


// ── Combined Members Tab ─────────────────────────────────────────────────────
export function CombinedMembersTab({ profile, onSwitchTab }) {
  const [sub, setSub] = useState('members');
  return (
    <div>
      {/* Pill switcher — same style as Inquiry tab */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, background: '#f3f4f6', borderRadius: 40, padding: 5, width: 'fit-content' }}>
        {[
          ['members', <SearchOutlined  style={{ fontSize: 14 }} />, 'Members'],
          ['city',    <CompassOutlined style={{ fontSize: 14 }} />, 'City Community'],
        ].map(([k, icon, label]) => (
          <button key={k} onClick={() => setSub(k)} style={{
            fontSize: 12, fontWeight: 800, padding: '7px 18px', borderRadius: 30,
            cursor: 'pointer', border: 'none', whiteSpace: 'nowrap',
            background: sub === k ? M : 'transparent',
            color: sub === k ? G : '#6b7280',
            display: 'flex', alignItems: 'center', gap: 7,
            boxShadow: sub === k ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
            transition: 'all 0.18s',
          }}>{icon}{label}</button>
        ))}
      </div>
      {sub === 'members' && <MemberDirectoryTab onSwitchTab={onSwitchTab} />}
      {sub === 'city'    && <CityCommunityTab profile={profile} />}
    </div>
  );
}

// ── Member Directory Tab (सदस्य खोजें) ───────────────────────────────────────
// Generic master autocomplete — village, tehsil, or district
export function MasterAutocomplete({ value, onChange, placeholder, endpoint, labelFn, subFn }) {
  const [query,       setQuery]       = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open,        setOpen]        = useState(false);
  const [searching,   setSearching]   = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback(async (q) => {
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${baseUrl}${endpoint}?q=${encodeURIComponent(q)}`, { headers: authHeader() });
      if (r.data.status) { setSuggestions(r.data.result || []); setOpen(true); }
    } catch {} finally { setSearching(false); }
  }, [endpoint]);

  const dQuery = useDebounce(query, 300);
  useEffect(() => { fetchSuggestions(dQuery); }, [dQuery, fetchSuggestions]);

  const select = (v) => {
    setQuery(labelFn(v));
    setSuggestions([]);
    setOpen(false);
    onChange(v);
  };

  const clear = () => { setQuery(''); setSuggestions([]); setOpen(false); onChange(null); };

  const iStyle = { width: '100%', height: 38, borderRadius: 8, border: `1px solid ${open ? M : '#d1d5db'}`, padding: '0 28px 0 10px', fontSize: 12, outline: 'none', boxSizing: 'border-box' };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input value={query} onChange={e => { setQuery(e.target.value); if (!e.target.value) clear(); }} placeholder={placeholder} style={iStyle} />
        {query && <button onMouseDown={clear} style={{ position: 'absolute', right: 7, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>}
        {searching && <span style={{ position: 'absolute', right: 26, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#9ca3af' }}>...</span>}
      </div>
      {open && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 200, maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
          {suggestions.map((v, i) => (
            <div key={i} onMouseDown={() => select(v)}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111827' }}>{labelFn(v)}</div>
              {subFn && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{subFn(v)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function MemberDirectoryTab({ onSwitchTab }) {
  const { t } = useLang();
  const [activeView,   setActiveView]   = useState('search'); // 'search' | 'friends'
  const [viewMode,     setViewMode]     = useState('card');   // 'card' | 'list'
  const [results,      setResults]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [total,        setTotal]        = useState(0);
  const [sending,      setSending]      = useState({});
  const [filters,      setFilters]      = useState({ search: '', village_hn: '', village_en: '', tehsil: '', district: '', gotra: '' });
  const [profileModal, setProfileModal] = useState(null);
  const [myContacts,   setMyContacts]   = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  // Suggestions — shown when no filter is active (like home Samaj Connect)
  const [suggestions,  setSuggestions]  = useState(null);
  const [sugLoading,   setSugLoading]   = useState(false);
  const dSearch    = useDebounce(filters.search,      400);
  const dVillageHn = useDebounce(filters.village_hn,  400);
  const dVillageEn = useDebounce(filters.village_en,  400);
  const dTehsil    = useDebounce(filters.tehsil,      400);
  const dDistrict  = useDebounce(filters.district,    400);
  const dGotra     = useDebounce(filters.gotra,       400);

  const hasFilters = !!dSearch;

  // Fetch suggestions on mount (same data as home Samaj Connect)
  useEffect(() => {
    setSugLoading(true);
    axios.get(`${baseUrl}/member/suggestions`, { headers: authHeader() })
      .then(r => { if (r.data.status) setSuggestions(r.data); })
      .catch(() => setSuggestions({}))
      .finally(() => setSugLoading(false));
  }, []); // eslint-disable-line

  const search = useCallback(async () => {
    if (!dSearch) { setResults([]); setTotal(0); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 30, search: dSearch });
      const res = await axios.get(`${baseUrl}/member/member-search?${params}`, { headers: authHeader() });
      if (res.data.status) { setResults(res.data.result || []); setTotal(res.data.total || 0); }
    } catch {} finally { setLoading(false); }
  }, [dSearch]);

  useEffect(() => { search(); }, [search]);

  const sendConnect = async (m) => {
    if (sending[m.id] || m.connection_status === 'accepted' || m.connection_status === 'pending') return;
    setSending(s => ({ ...s, [m.id]: true }));
    try {
      await axios.post(`${baseUrl}/member/contact-request`, { to_user_id: m.id }, { headers: authHeader() });
      notification.success({ message: '🤝 संपर्क अनुरोध भेजा गया!' });
      setResults(prev => prev.map(r => r.id === m.id ? { ...r, connection_status: 'pending' } : r));
    } catch (err) {
      notification.warning({ message: err.response?.data?.message || 'Error' });
    } finally { setSending(s => ({ ...s, [m.id]: false })); }
  };

  const ConnectBtn = ({ m, block = false }) => {
    const s = m.connection_status;
    const baseStyle = { fontSize: 10, fontWeight: 700, borderRadius: 7, padding: '5px 10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 3, width: block ? '100%' : 'auto' };
    if (s === 'accepted') return (
      <span style={{ ...baseStyle, color: '#15803d', background: '#dcfce7', border: '1px solid #86efac' }}>✅ जुड़े हैं</span>
    );
    if (s === 'pending') return (
      <span style={{ ...baseStyle, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a' }}>⏳ प्रतीक्षारत</span>
    );
    return (
      <button onClick={() => sendConnect(m)} disabled={!!sending[m.id]}
        style={{ ...baseStyle, color: M, background: '#fff5f5', border: `1px solid ${M}`,
          cursor: 'pointer', opacity: sending[m.id] ? 0.6 : 1 }}>
        <LinkOutlined /> {sending[m.id] ? '...' : 'संपर्क जोड़ें'}
      </button>
    );
  };

  const F = (key) => (e) => setFilters(f => ({ ...f, [key]: e.target.value }));
  const iStyle = { width: '100%', height: 38, borderRadius: 8, border: '1px solid #d1d5db', padding: '0 10px', fontSize: 12, outline: 'none', boxSizing: 'border-box' };

  const fetchMyContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/contacts`, { headers: authHeader() });
      if (r.data.status) setMyContacts(r.data.result || []);
    } catch {} finally { setContactsLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { if (activeView === 'friends') fetchMyContacts(); }, [activeView, fetchMyContacts]);

  const ini = n => (n || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[
          { key: 'search', label: '🔍 ' + t('सदस्य खोजें', 'Search') },
          { key: 'friends', label: '👥 ' + t('मेरे मित्र', 'My Contacts') },
        ].map(v => (
          <button key={v.key} onClick={() => setActiveView(v.key)} style={{
            flex: 1, padding: '9px 0', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
            background: activeView === v.key ? M : '#f3f4f6',
            color: activeView === v.key ? G : '#6b7280',
          }}>{v.label}</button>
        ))}
      </div>

      {/* Profile modal */}
      {profileModal && (
        <MemberProfileModal memberId={profileModal} onClose={() => setProfileModal(null)} onSwitchTab={onSwitchTab} />
      )}

      {/* ── My Friends view ── */}
      {activeView === 'friends' && (
        <div>
          {contactsLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}
          {!contactsLoading && myContacts.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>👥</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>अभी कोई मित्र नहीं है</div>
              <div style={{ fontSize: 11 }}>"सदस्य खोजें" tab में सदस्यों को जोड़ें</div>
              <button onClick={() => setActiveView('search')} style={{ marginTop: 14, background: M, color: G, border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                सदस्य खोजें →
              </button>
            </div>
          )}
          {!contactsLoading && myContacts.map((c, i) => (
            <div key={i} onClick={() => setProfileModal(c.member_id)} style={{
              background: '#fff', borderRadius: 12, marginBottom: 8,
              border: '1px solid #e5e7eb', padding: '10px 14px',
              display: 'flex', gap: 12, alignItems: 'center', cursor: 'pointer',
            }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {c.photo
                  ? <img src={c.photo} alt={c.name} style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}` }} />
                  : <div style={{ width: 46, height: 46, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: G }}>{ini(c.name)}</div>}
                <span style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, background: '#22c55e', borderRadius: '50%', border: '2px solid #fff' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{c.name}</div>
                {c.father_name && <div style={{ fontSize: 11, color: '#6b7280' }}>पिता: {c.father_name}</div>}
                <div style={{ fontSize: 11, color: '#9ca3af' }}>
                  <EnvironmentOutlined style={{ marginRight: 3 }} />
                  {[c.village, c.district].filter(Boolean).join(', ')}
                </div>
              </div>
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                {c.phone_no && (
                  <a href={`tel:${c.phone_no}`} onClick={e => e.stopPropagation()} style={{ fontSize: 11, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 6, padding: '3px 8px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <PhoneOutlined /> {c.phone_no}
                  </a>
                )}
                <a href={`https://wa.me/91${c.phone_no}`} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#25d366', borderRadius: 6, padding: '2px 7px', textDecoration: 'none' }}>
                  💬 WhatsApp
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Search view ── */}
      {activeView === 'search' && <div>

      {/* ── Universal search box — always at top ── */}
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <SearchOutlined style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 15, zIndex: 1 }} />
        <input
          value={filters.search}
          onChange={F('search')}
          placeholder={t('नाम, गाँव, जिला, गोत्र, व्यवसाय…', 'Name, village, district, gotra, occupation…')}
          style={{
            width: '100%', boxSizing: 'border-box',
            paddingLeft: 38, paddingRight: filters.search ? 36 : 14,
            height: 44, border: `2px solid ${filters.search ? M : '#e5e7eb'}`,
            borderRadius: 12, fontSize: 13, outline: 'none', color: '#374151',
            background: '#fff', transition: 'border-color 0.15s',
          }}
        />
        {filters.search && (
          <button
            onClick={() => setFilters(f => ({ ...f, search: '' }))}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: '#e5e7eb', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
            ×
          </button>
        )}
      </div>

      {/* ── DEFAULT: People you may know (shown when no filter is active) ── */}
      {!hasFilters && (
        <div style={{ marginBottom: 16 }}>
          {sugLoading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
          {!sugLoading && suggestions && (() => {
            const seenIds = new Set();
            const dedup = arr => (arr || []).filter(m => { if (seenIds.has(m.id)) return false; seenIds.add(m.id); return true; });
            const all = dedup([
              ...(suggestions.gotra_suggestions   || []),
              ...(suggestions.village_suggestions || []),
              ...(suggestions.nearby_suggestions  || []),
              ...(suggestions.other_suggestions   || []),
            ]);
            if (all.length === 0) return null;
            return (
              <>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#374151', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BulbOutlined style={{ color: M }} /> {t('लोग जो आप जान सकते हैं', 'People you may know')}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                  {all.map(m => {
                    const location = [m.village, m.district].filter(Boolean).join(', ');
                    return (
                      <div key={m.id} onClick={() => setProfileModal(m.id)} style={{
                        background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb',
                        padding: '14px 10px 10px', cursor: 'pointer', textAlign: 'center',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                      }}>
                        {/* Photo */}
                        <div style={{ width: 58, height: 58, borderRadius: '50%', overflow: 'hidden', marginBottom: 8, border: `2.5px solid ${M}`, flexShrink: 0 }}>
                          {m.photo
                            ? <img src={m.photo} alt={m.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                            : <div style={{ width: '100%', height: '100%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: G }}>
                                {(m.name || '?')[0].toUpperCase()}
                              </div>}
                        </div>
                        {/* Name */}
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#111827', marginBottom: 2, lineHeight: 1.3, width: '100%', wordBreak: 'break-word' }}>
                          {m.name || '—'}
                        </div>
                        {/* Father */}
                        {m.father_name && (
                          <div style={{ fontSize: 10, color: '#6b7280', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                            पि. {m.father_name}
                          </div>
                        )}
                        {/* Gotra */}
                        {m.gotra && (
                          <div style={{ fontSize: 10, color: '#7c3aed', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                            गोत्र: {m.gotra}
                          </div>
                        )}
                        {/* Location */}
                        {location && (
                          <div style={{ fontSize: 10, color: '#9ca3af', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8 }}>
                            <EnvironmentOutlined style={{ fontSize: 9 }} /> {location}
                          </div>
                        )}
                        <div style={{ flex: 1 }} />
                        {/* Connect button */}
                        <div style={{ width: '100%', marginTop: 4 }} onClick={e => e.stopPropagation()}>
                          <ConnectBtn m={m} block />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}


      {loading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}


      {!loading && results.length > 0 && (
        <>
          {/* Result count + view toggle */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#6b7280' }}>
              {total} {t('सदस्य मिले', 'members found')}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {[{ k: 'card', icon: '⊞' }, { k: 'list', icon: '☰' }].map(v => (
                <button key={v.k} onClick={() => setViewMode(v.k)} style={{
                  width: 30, height: 30, border: `1px solid ${viewMode === v.k ? M : '#e5e7eb'}`,
                  borderRadius: 7, background: viewMode === v.k ? M : '#fff',
                  color: viewMode === v.k ? '#fff' : '#6b7280',
                  fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{v.icon}</button>
              ))}
            </div>
          </div>

          {/* CARD VIEW — 2-column grid */}
          {viewMode === 'card' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {results.map(m => {
                const cf = countryFlag(m.current_country);
                const isNRI = cf?.isAbroad;
                const location = [m.village, m.district].filter(Boolean).join(', ');
                return (
                  <div key={m.id} onClick={() => setProfileModal(m.id)} style={{
                    background: '#fff', borderRadius: 14,
                    border: isNRI ? '1.5px solid #fbbf24' : '1px solid #e5e7eb',
                    padding: '14px 10px 10px',
                    boxShadow: isNRI ? '0 2px 10px rgba(251,191,36,0.15)' : '0 1px 4px rgba(0,0,0,0.04)',
                    cursor: 'pointer', textAlign: 'center', position: 'relative',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                  }}>
                    {/* NRI flag */}
                    {isNRI && cf?.flag && (
                      <span style={{ position: 'absolute', top: 8, right: 8, fontSize: 14 }}>{cf.flag}</span>
                    )}
                    {/* Photo */}
                    <div style={{ width: 58, height: 58, borderRadius: '50%', overflow: 'hidden', marginBottom: 8, border: `2.5px solid ${isNRI ? '#fbbf24' : M}`, flexShrink: 0 }}>
                      {m.photo
                        ? <img src={m.photo} alt={m.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />
                        : <div style={{ width: '100%', height: '100%', background: isNRI ? '#78350f' : M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 800, color: G }}>
                            {(m.name || '?')[0].toUpperCase()}
                          </div>
                      }
                    </div>
                    {/* Name */}
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#111827', marginBottom: 2, lineHeight: 1.3, width: '100%', wordBreak: 'break-word', minHeight: 16 }}>
                      {m.name || '—'}
                    </div>
                    {/* Member # */}
                    {m.membership_no && (
                      <div style={{ fontSize: 9, fontWeight: 700, color: M, background: `${M}12`, borderRadius: 20, padding: '1px 8px', marginBottom: 3 }}>
                        #{m.membership_no}
                      </div>
                    )}
                    {/* Father */}
                    {m.father_name && (
                      <div style={{ fontSize: 10, color: '#6b7280', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                        पि. {m.father_name}
                      </div>
                    )}
                    {/* Gotra */}
                    {m.gotra && (
                      <div style={{ fontSize: 10, color: '#7c3aed', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 }}>
                        गोत्र: {m.gotra}
                      </div>
                    )}
                    {/* Location */}
                    {location && (
                      <div style={{ fontSize: 10, color: '#9ca3af', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 8 }}>
                        <EnvironmentOutlined style={{ fontSize: 9 }} /> {location}
                      </div>
                    )}
                    {/* Spacer pushes button to bottom */}
                    <div style={{ flex: 1 }} />
                    {/* Connect button */}
                    <div style={{ width: '100%', marginTop: 6 }} onClick={e => e.stopPropagation()}>
                      <ConnectBtn m={m} block />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* LIST VIEW — original rows */}
          {viewMode === 'list' && results.map(m => {
            const cf = countryFlag(m.current_country);
            const isNRI = cf?.isAbroad;
            return (
              <div key={m.id} onClick={() => setProfileModal(m.id)} style={{
                background: '#fff', borderRadius: 12, marginBottom: 8,
                border: isNRI ? `1.5px solid #fbbf24` : '1px solid #e5e7eb',
                padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'center',
                boxShadow: isNRI ? '0 2px 10px rgba(251,191,36,0.15)' : 'none', cursor: 'pointer',
              }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  {m.photo
                    ? <img src={m.photo} alt={m.name} style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${isNRI ? '#fbbf24' : M}` }} />
                    : <div style={{ width: 46, height: 46, borderRadius: '50%', background: isNRI ? '#78350f' : M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: G }}>
                        {(m.name || '?')[0].toUpperCase()}
                      </div>
                  }
                  {isNRI && cf?.flag && (
                    <span style={{ position: 'absolute', bottom: -4, right: -4, fontSize: 16, lineHeight: 1, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', padding: 1 }}>{cf.flag}</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{m.name}</div>
                    {isNRI && <span style={{ fontSize: 9, fontWeight: 700, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', borderRadius: 20, padding: '1px 7px' }}>🌍 NRI · {m.current_country}</span>}
                    <LastSeenBadge ts={m.last_active_at} />
                  </div>
                  {m.father_name && <div style={{ fontSize: 11, color: '#6b7280' }}>{t('पिता', 'Father')}: {m.father_name}</div>}
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    {[m.village, m.district, m.state].filter(Boolean).join(', ')}
                    {m.gotra ? ` • ${t('गोत्र', 'Gotra')}: ${m.gotra}` : ''}
                  </div>
                  {isNRI && m.current_city && <div style={{ fontSize: 10, color: '#b45309', marginTop: 2, fontWeight: 600 }}>📍 {t('वर्तमान शहर', 'Currently in')}: {m.current_city}</div>}
                </div>
                <div onClick={e => e.stopPropagation()} style={{ textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  {m.membership_no && <div style={{ fontSize: 10, color: '#9ca3af' }}>#{m.membership_no}</div>}
                  <ConnectBtn m={m} />
                </div>
              </div>
            );
          })}
        </>
      )}
      {!loading && hasFilters && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
          {t('कोई सदस्य नहीं मिला', 'No members found')}
        </div>
      )}
      </div>}
    </div>
  );
}

